/**
 * EditInvoiceLinesModal Component
 *
 * Modal shell around `InvoiceLinesForm`. Owns the line list, the save
 * lifecycle (`saving` flag, save-error capture), and the modal chrome
 * (header, footer with item count and Save/Cancel). The form handles the
 * actual editing UI.
 *
 * Uses `useTranslation('modules.modals.editInvoiceLines')` for chrome labels.
 */
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Icon } from '../../components/icons/Icon';
import { useEnterSubmit } from '../../hooks';
import { useTranslation } from '../../providers/UIProvider';
import { getTestId, type TestIdProps } from '../../utils';
import type { ValidationIssue } from '../../types';
import {
  InvoiceLinesForm,
  type InvoiceLineExtraField,
  type InvoiceLineFormItem,
  type InvoiceLinesFormData,
  type RecomputeInvoiceLineFn,
  type VatRateOption
} from '../forms/invoice-lines/InvoiceLinesForm';
import { toCleanInvoiceLines } from '../forms/invoice-lines/utils';

// Re-export types/classes that previously lived in this file so existing
// imports from `EditInvoiceLinesModal` keep working.
export {
  InvoiceLinesValidationError,
  type InvoiceLineFormItem,
  type InvoiceLinesFormData,
  type VatRateOption,
  type InvoiceLine
} from '../forms/invoice-lines/InvoiceLinesForm';

export interface EditInvoiceLinesModalProps extends TestIdProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: InvoiceLinesFormData) => Promise<void>;
  currentLines: InvoiceLineFormItem[];
  /** Initial value for the form-level "unit prices include VAT" toggle.
   * Defaults to `false` (net pricing). */
  currentIsUnitPriceInclVat?: boolean;
  currencyCode?: string | null;
  /** Available VAT rate options for dropdown. When provided, vatRate renders as Select; otherwise as text Input. */
  vatRateOptions?: readonly VatRateOption[];
  /** External validation issues with dot-path identifiers
   * (`invoiceLines.<index>.<field>` or `invoiceLines.<index>` for line-level).
   * Issues outside the `invoiceLines.*` namespace are ignored by the form
   * (except `path === ''` which renders as a banner). */
  errors?: ValidationIssue[];
  /** Optional async hook that resolves backend-computed `InvoiceLineAmounts`
   * for a single line. Forwarded to `InvoiceLinesForm` — when set, typed totals
   * that disagree with the computed amounts are flagged as warnings. */
  onRecomputeLine?: RecomputeInvoiceLineFn;
  /** Host fields drawn on every line, forwarded to `InvoiceLinesForm`. */
  lineFields?: readonly InvoiceLineExtraField[];
}

export const EditInvoiceLinesModal: React.FC<EditInvoiceLinesModalProps> = ({
  open,
  onClose,
  onSave,
  currentLines,
  currentIsUnitPriceInclVat = false,
  vatRateOptions,
  errors: externalIssues = [],
  onRecomputeLine,
  lineFields,
  testId,
  id
}) => {
  const t = useTranslation('modules.modals.editInvoiceLines');

  const [lines, setLines] = useState<InvoiceLineFormItem[]>([]);
  const [isUnitPriceInclVat, setIsUnitPriceInclVat] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ValidationIssue | null>(null);

  // Mirror the latest form data so `handleSave` reads the value committed by a
  // synchronous blur (Enter-to-save) instead of the stale state closure.
  const linesRef = useRef(lines);
  const inclVatRef = useRef(isUnitPriceInclVat);

  const updateForm = (nextLines: InvoiceLineFormItem[], nextInclVat: boolean) => {
    linesRef.current = nextLines;
    inclVatRef.current = nextInclVat;
    setLines(nextLines);
    setIsUnitPriceInclVat(nextInclVat);
  };

  // Seed the form only when the modal opens. Re-seeding on every `currentLines` reference would
  // throw the draft away whenever the host re-renders (e.g. a background refetch).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    const initialLines = currentLines.length ? currentLines.map(line => ({ ...line })) : [];
    linesRef.current = initialLines;
    inclVatRef.current = currentIsUnitPriceInclVat;
    setLines(initialLines);
    setIsUnitPriceInclVat(currentIsUnitPriceInclVat);
    setSaveError(null);
  }, [open, currentLines, currentIsUnitPriceInclVat]);

  const combinedErrors = useMemo<ValidationIssue[]>(
    () => (saveError ? [...externalIssues, saveError] : externalIssues),
    [externalIssues, saveError]
  );

  const handleClose = () => {
    if (!saving) {
      setLines([]);
      setSaveError(null);
      onClose();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        invoiceLines: toCleanInvoiceLines(linesRef.current),
        isUnitPriceInclVat: inclVatRef.current
      });
      handleClose();
    } catch (error) {
      setSaveError({
        __typename: 'ValidationIssue',
        code: 'SAVE_ERROR',
        groups: [],
        message: error instanceof Error ? error.message : t('errors.saveError'),
        path: '',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEnterKeyDown = useEnterSubmit(handleSave);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size='half'
      className='w-[90vw]! max-w-7xl flex flex-col'
      testId={getTestId({ testId, id })}
    >
      <Modal.Header
        label={t('title')}
        description={t('description')}
        onClose={handleClose}
        variant='white'
        icon={<Icon name='file-lines' className='w-6 h-6 text-primary-600' />}
      />

      <Modal.Content className='p-0 bg-background-muted' size='full'>
        <div className='py-md px-md sm:py-lg sm:px-lg' onKeyDown={handleEnterKeyDown}>
          <div className='bg-white rounded-lg border border-border-base p-md sm:p-lg'>
            <InvoiceLinesForm
              value={lines}
              isUnitPriceInclVat={isUnitPriceInclVat}
              onChange={data => updateForm(data.invoiceLines, data.isUnitPriceInclVat)}
              errors={combinedErrors}
              vatRateOptions={vatRateOptions}
              disabled={saving}
              onRecomputeLine={onRecomputeLine}
              lineFields={lineFields}
            />
          </div>
        </div>
      </Modal.Content>

      <Modal.Footer variant='white' className='justify-start! p-0!'>
        <div className='flex flex-row justify-between items-center w-full px-lg py-md'>
          <div className='text-sm text-foreground-muted mr-auto'>
            {t('itemCount', { count: lines.length })}
          </div>
          <div className='flex items-center gap-md'>
            <Button variant='ghost' size='md' onClick={handleClose} disabled={saving}>
              {t('actions.cancel')}
            </Button>
            <Button variant='primary' size='md' onClick={handleSave} disabled={saving}>
              {saving ? t('actions.saving') : t('actions.save')}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

EditInvoiceLinesModal.displayName = 'EditInvoiceLinesModal';
